// src/pages/OrgChart/OrgChartPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../../services/api';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Stack,
  Button,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Collapse,
  List,
  ListItemButton,
  ToggleButton,
  ToggleButtonGroup,
  ButtonBase,
  IconButton,
  Divider,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import LayersIcon from '@mui/icons-material/Layers';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import UnfoldMoreDoubleIcon from '@mui/icons-material/UnfoldMoreDouble'; // expand all
import UnfoldLessDoubleIcon from '@mui/icons-material/UnfoldLessDouble';   // collapse all
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

/* ===================== Small helpers ===================== */
const statusChipColor = (s) =>
  s === 'active' ? 'success' : s === 'on_leave' ? 'warning' : 'default';

const deptColor = (theme, name = '') => {
  // deterministic light color from department string
  const palette = [
    theme.palette.info.light,
    theme.palette.success.light,
    theme.palette.warning.light,
    theme.palette.secondary?.light || '#c3b8ff',
    theme.palette.primary.light,
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
};

const highlightText = (text = '', term = '') => {
  if (!term) return text;
  const parts = String(text).split(new RegExp(`(${term.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')})`, 'ig'));
  return parts.map((p, i) =>
    i % 2 === 1 ? (
      <Box key={i} component="mark" sx={{ px: 0.25, borderRadius: 0.5, bgColor: 'transparent', background: 'rgba(255, 235, 59, 0.5)' }}>
        {p}
      </Box>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
};

const collectIds = (root) => {
  const ids = new Set();
  const visit = (n) => {
    if (Array.isArray(n)) return n.forEach(visit);
    ids.add(n.id);
    (n.children || []).forEach(visit);
  };
  if (root) visit(root);
  return ids;
};

/* ===================== Shared Node Label ===================== */
const NodeLabel = ({ n, childrenCount = 0, query = '', onFocus }) => {
  const theme = useTheme();
  const strip = deptColor(theme, n.department);
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        p: 1,
        borderRadius: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        background:
          theme.palette.mode === 'dark'
            ? alpha('#fff', 0.02)
            : alpha(theme.palette.primary.main, 0.03),
        boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* colored strip by department */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: strip,
        }}
      />
      <PersonOutlineIcon sx={{ fontSize: 18, opacity: 0.7, ml: 0.5 }} />
      <Box sx={{ minWidth: 160 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {highlightText(n.name, query)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {highlightText(n.title || '—', query)}
        </Typography>
      </Box>

      <Chip
        size="small"
        variant="outlined"
        label={highlightText(n.department || '—', query)}
        icon={<CorporateFareIcon sx={{ fontSize: 14 }} />}
      />
      <Chip size="small" color={statusChipColor(n.status)} label={n.status} />
      <Chip
        size="small"
        variant="outlined"
        label={n.employeeCode}
        icon={<LayersIcon sx={{ fontSize: 14 }} />}
      />
      {childrenCount > 0 && (
        <Chip
          size="small"
          label={`${childrenCount}`}
          title="Direct reports"
          sx={{ ml: 'auto', mr: 0.5 }}
        />
      )}
      {!!onFocus && (
        <Tooltip title="Set as root">
          <IconButton size="small" onClick={onFocus} sx={{ ml: 0.5 }}>
            <CenterFocusStrongIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

/* ===================== Filtering ===================== */
function filterTree(root, term) {
  if (!term) return root;
  const t = term.toLowerCase();
  const hit = (node) =>
    (node.name || '').toLowerCase().includes(t) ||
    (node.title || '').toLowerCase().includes(t) ||
    (node.department || '').toLowerCase().includes(t) ||
    (node.employeeCode || '').toLowerCase().includes(t);

  const visit = (node) => {
    const children = (node.children || []).map(visit).filter(Boolean);
    if (hit(node) || children.length > 0) return { ...node, children };
    return null;
  };

  return Array.isArray(root) ? root.map(visit).filter(Boolean) : visit(root);
}

/* ===================== View A: List (accordion) ===================== */
function TreeList({ data, query, onFocusRoot, expandAllKey, collapseAllKey }) {
  const [expanded, setExpanded] = useState({});

  // expand/collapse all when keys change
  useEffect(() => {
    const ids = collectIds(data);
    const map = {};
    ids.forEach((id) => (map[id] = true));
    setExpanded(map);
  }, [expandAllKey, data]);

  useEffect(() => {
    setExpanded({});
  }, [collapseAllKey]);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const renderNode = (node, depth = 0) => {
    if (Array.isArray(node)) return node.map((n) => renderNode(n, 0));
    const kids = Array.isArray(node.children) ? node.children : [];
    const hasChildren = kids.length > 0;
    const isOpen = !!expanded[node.id];

    return (
      <React.Fragment key={node.id}>
        <ListItemButton
          onClick={() => (hasChildren ? toggle(node.id) : null)}
          sx={{
            pl: 1 + depth * 2,
            alignItems: 'flex-start',
            '&:hover': { backgroundColor: (t) => alpha(t.palette.primary.main, 0.06) },
          }}
        >
          <Box sx={{ width: 24, mt: 0.5 }}>
            {hasChildren ? (isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />) : null}
          </Box>
          <Box sx={{ ml: 1, flex: 1 }}>
            <NodeLabel
              n={node}
              childrenCount={kids.length}
              query={query}
              onFocus={() => onFocusRoot(node.employeeCode)}
            />
          </Box>
        </ListItemButton>

        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {kids.map((ch) => renderNode(ch, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <List disablePadding sx={{ maxHeight: 680, overflow: 'auto', borderRadius: 2 }}>
      {Array.isArray(data) ? data.map((n) => renderNode(n)) : renderNode(data)}
    </List>
  );
}

/* ===================== View B: Family Tree (horizontal) ===================== */
function FamilyTree({ data, query, onFocusRoot, zoom, expandAllKey, collapseAllKey }) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const ids = collectIds(data);
    const map = {};
    ids.forEach((id) => (map[id] = true));
    setExpanded(map);
  }, [expandAllKey, data]);

  useEffect(() => {
    setExpanded({});
  }, [collapseAllKey]);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const NodeCard = ({ node, childrenCount }) => (
    <ButtonBase onClick={() => toggle(node.id)} sx={{ borderRadius: 2 }}>
      <Box sx={{ minWidth: 240, maxWidth: 320 }}>
        <NodeLabel
          n={node}
          childrenCount={childrenCount}
          query={query}
          onFocus={() => onFocusRoot(node.employeeCode)}
        />
      </Box>
    </ButtonBase>
  );

  const ChildrenConnector = ({ count }) => (
    <Box sx={{ position: 'relative', height: 16, my: 1 }}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 2,
          height: '100%',
          bgcolor: theme.palette.divider,
        }}
      />
      {count > 0 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: '10%',
            right: '10%',
            height: 2,
            bgcolor: theme.palette.divider,
          }}
        />
      )}
    </Box>
  );

  const VerticalSpacer = () => <Box sx={{ height: 8 }} />;

  const renderNode = (node) => {
    if (Array.isArray(node)) {
      return (
        <Box
          key="forest"
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}
        >
          {node.map((n) => (
            <Box key={n.id}>{renderNode(n)}</Box>
          ))}
        </Box>
      );
    }

    const kids = Array.isArray(node.children) ? node.children : [];
    const open = expanded[node.id] ?? true;

    return (
      <Box
        key={node.id}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: 1,
          py: 1.5,
        }}
      >
        <NodeCard node={node} childrenCount={kids.length} />
        {kids.length > 0 && (
          <>
            <ChildrenConnector count={kids.length} />
            {open && (
              <>
                <VerticalSpacer />
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 3,
                    p: 1,
                  }}
                >
                  {kids.map((ch) => (
                    <Box key={ch.id} sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -24,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 2,
                          height: 24,
                          bgcolor: theme.palette.divider,
                        }}
                      />
                      {renderNode(ch)}
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        maxHeight: 680,
        overflow: 'auto',
        p: 1,
        '&::-webkit-scrollbar': { height: 10, width: 10 },
      }}
    >
      <Box
        sx={{
          minWidth: 900,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          transform: `scale(${zoom})`,
          transformOrigin: 'top center',
          transition: 'transform 120ms ease',
        }}
      >
        {renderNode(data)}
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', textAlign: 'center', mt: 1 }}
      >
        Tip: Click a card to collapse/expand its branch. Use the zoom controls to adjust view.
      </Typography>
    </Box>
  );
}

/* ===================== Page ===================== */
export default function OrgChartPage() {
  const theme = useTheme();
  const [rootCode, setRootCode] = useState('');
  const [depth, setDepth] = useState(6);
  const [includeTerminated, setIncludeTerminated] = useState(false);
  const [query, setQuery] = useState('');

  const [view, setView] = useState('list'); // 'list' | 'family'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // controls state
  const [expandAllKey, setExpandAllKey] = useState(0);
  const [collapseAllKey, setCollapseAllKey] = useState(0);
  const [zoom, setZoom] = useState(1);

  const headerGrad = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${
    theme.palette.secondary2?.main || '#2389ba'
  } 100%)`;

  const fetchData = async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await api.get('/admin/org-chart', {
        params: {
          root: rootCode || undefined,
          depth,
          include_terminated: includeTerminated ? 1 : 0,
          format: 'tree',
        },
      });
      setData(res.data);
    } catch (e) {
      console.error(e);
      setErr('Failed to load org chart.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => filterTree(data, query), [data, query]);

  const onFocusRoot = useCallback(
    (employeeCode) => {
      setRootCode(employeeCode);
      // keep depth and filters; fetch fresh tree from this root
      setTimeout(fetchData, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [depth, includeTerminated]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          background: headerGrad,
          color: '#fff',
          py: { xs: 3, md: 4 },
          px: { xs: 2, md: 4 },
          borderRadius: 2,
          mb: 3,
          boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          Company Org Chart
        </Typography>
        <Typography sx={{ opacity: 0.9 }}>
          Explore reporting lines. Search, expand/collapse, or switch to a family-tree view.
        </Typography>

        {/* Controls */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          sx={{ mt: 2 }}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <TextField
            size="small"
            label="Employee Code (root)"
            placeholder="e.g., 10462"
            value={rootCode}
            onChange={(e) => setRootCode(e.target.value)}
            sx={{ minWidth: 220, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}
            InputProps={{ style: { color: '#fff' } }}
            InputLabelProps={{ style: { color: 'rgba(255,255,255,0.85)' } }}
          />
          <TextField
            size="small"
            type="number"
            label="Depth"
            value={depth}
            onChange={(e) => setDepth(Math.max(1, Math.min(12, Number(e.target.value) || 1)))}
            sx={{ width: 120, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}
            InputProps={{ style: { color: '#fff' } }}
            InputLabelProps={{ style: { color: 'rgba(255,255,255,0.85)' } }}
          />
          <TextField
            size="small"
            label="Search name/title/department/code"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ flex: 1, minWidth: 260, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}
            InputProps={{
              style: { color: '#fff' },
              startAdornment: <SearchIcon sx={{ mr: 1, opacity: 0.85 }} />,
            }}
            InputLabelProps={{ style: { color: 'rgba(255,255,255,0.85)' } }}
          />

          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            {/* View switcher */}
            <ToggleButtonGroup
              size="small"
              color="standard"
              value={view}
              exclusive
              onChange={(_e, v) => v && setView(v)}
              sx={{
                background: 'rgba(255,255,255,0.12)',
                '& .MuiToggleButton-root': { color: '#fff', borderColor: alpha('#fff', 0.4) },
                '& .Mui-selected': { background: alpha('#000', 0.18) },
              }}
            >
              <ToggleButton value="list">List</ToggleButton>
              <ToggleButton value="family">Family Tree</ToggleButton>
            </ToggleButtonGroup>

            {/* Expand/Collapse all */}
            <Tooltip title="Expand all">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setExpandAllKey((k) => k + 1)}
                  sx={{ color: '#fff' }}
                >
                  <UnfoldMoreDoubleIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Collapse all">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setCollapseAllKey((k) => k + 1)}
                  sx={{ color: '#fff' }}
                >
                  <UnfoldLessDoubleIcon />
                </IconButton>
              </span>
            </Tooltip>

            {/* Zoom (family view) */}
            {view === 'family' && (
              <>
                <Divider
                  flexItem
                  orientation="vertical"
                  sx={{ borderColor: alpha('#fff', 0.3), mx: 0.5 }}
                />
                <Tooltip title="Zoom out">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => setZoom((z) => Math.max(0.6, +(z - 0.1).toFixed(2)))}
                      sx={{ color: '#fff' }}
                    >
                      <ZoomOutIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <Tooltip title="Zoom in">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(2)))}
                      sx={{ color: '#fff' }}
                    >
                      <ZoomInIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Reset zoom">
                  <span>
                    <IconButton size="small" onClick={() => setZoom(1)} sx={{ color: '#fff' }}>
                      <RestartAltIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}

            {/* Data actions */}
            <Tooltip title="Reload">
              <span>
                <Button
                  onClick={fetchData}
                  startIcon={<RefreshIcon />}
                  variant="outlined"
                  sx={{
                    color: '#fff',
                    borderColor: alpha('#fff', 0.6),
                    '&:hover': { borderColor: '#fff', background: alpha('#000', 0.1) },
                  }}
                >
                  Refresh
                </Button>
              </span>
            </Tooltip>
            <Button
              onClick={() => {
                setQuery('');
                fetchData();
              }}
              variant="contained"
            >
              Apply
            </Button>
          </Stack>
        </Stack>

        {/* Legend */}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          <Chip size="small" color="success" label="Active" />
          <Chip size="small" color="warning" label="On Leave" />
          <Chip size="small" variant="outlined" label="Terminated / Other" />
          <Chip size="small" variant="outlined" label="Number = Direct reports" />
          <Chip size="small" variant="outlined" label="Left color = Department" />
          <Chip
            size="small"
            label={`Include terminated: ${includeTerminated ? 'Yes' : 'No'}`}
          />
          <Button
            size="small"
            variant="text"
            sx={{ color: '#fff', textDecoration: 'underline' }}
            onClick={() => setIncludeTerminated((v) => !v)}
          >
            Toggle terminated
          </Button>
        </Stack>
      </Box>

      {/* Content */}
      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <CircularProgress />
          <Typography sx={{ mt: 1 }} color="text.secondary">
            Loading org chart…
          </Typography>
        </Box>
      ) : !filtered || (Array.isArray(filtered) && filtered.length === 0) ? (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 3, md: 5 },
            textAlign: 'center',
            borderRadius: 3,
            background:
              theme.palette.mode === 'dark'
                ? alpha('#fff', 0.02)
                : alpha(theme.palette.primary.light, 0.06),
          }}
        >
          <Typography variant="h6">No results</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Try changing the root, depth, or search term.
          </Typography>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1, md: 2 },
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            background:
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : theme.palette.background.paper,
          }}
        >
          {view === 'family' ? (
            <FamilyTree
              data={filtered}
              query={query}
              onFocusRoot={onFocusRoot}
              zoom={zoom}
              expandAllKey={expandAllKey}
              collapseAllKey={collapseAllKey}
            />
          ) : (
            <TreeList
              data={filtered}
              query={query}
              onFocusRoot={onFocusRoot}
              expandAllKey={expandAllKey}
              collapseAllKey={collapseAllKey}
            />
          )}
        </Paper>
      )}
    </Box>
  );
}
